import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import { Big } from 'big.js';
import * as _ from 'lodash';

function roundTo(n: number, digits: number): number {
  const v = Number(n);
  if (isNaN(v)) return 0;
  return Number(v.toFixed(digits));
}
function totalToReinvest(supportsDivestment: any[]): number {
  const total = (supportsDivestment || []).reduce(
    (acc, item) =>
      acc +
      (item?.amountRepartitionStock ? Number(item.amountRepartitionStock) : 0),
    0,
  );
  return Number.isFinite(total) ? new Big(total).round(2).toNumber() : 0;
}
function maxFractionDigits(maxDigits: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const s = String(raw).replace(',', '.');
    if (!/^\d*\.?\d*$/.test(s)) return { number: true };
    const [, decimals] = s.split('.');
    if (decimals && decimals.length > maxDigits) {
      return { maxFractionDigits: { max: maxDigits } };
    }
    return null;
  };
}

// Tolerance for treating sum-of-percent as exactly 100 (handles 4-decimal drift).
const FULL_PERCENT_TOLERANCE = 0.001;

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = { ...option };
    const options: AbstractControlOptions = {};

    const supportsReinvestmentStock: UntypedFormGroup[] = [];
    const reinvestmentStock = entity?.parameters?.supportsInvestmentStock || [];

    if (reinvestmentStock) {
      _.forEach(reinvestmentStock, support =>
        supportsReinvestmentStock.push(
          this.formBuilder.group(this.createModel(support)),
        ),
      );
    }

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        currentAmountStock: [
          entity?.parameters?.currentSupportRepartition?.totalAmount || null,
        ],
        supportsDivestment: [entity?.parameters?.supportsDivestment || []],
        arbitrageType: [entity?.parameters?.arbitrageType || null],
        supportsInvestmentStock: this.formBuilder.array(
          supportsReinvestmentStock,
          Validators.required,
        ),
      }),
    };

    const productForm = this.formBuilder.group(formConfig, options);

    this.attachReinvestmentSyncProduct(productForm);

    return productForm;
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  public createModel(item) {
    return {
      id: [item ? item.id : null],
      name: [item ? item.name : null],
      type: [item ? item.type : null],
      isin: [item ? item.isin : null],
      index: [item?.index || null],
      details: [item ? item.details : null],

      repartitionStock: [
        item && !_.isNil(item.repartitionStock)
          ? roundTo(item.repartitionStock * 100, 4)
          : 0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          maxFractionDigits(4),
        ],
      ],

      repartitionFlux: [
        item && item.repartitionFlux ? item.repartitionFlux : null,
      ],

      amountRepartitionStock: [
        item && item.amountRepartitionStock
          ? roundTo(item.amountRepartitionStock, 2)
          : 0,
        [Validators.required],
      ],

      amountRepartitionFlux: [
        item && item.amountRepartitionFlux ? item.amountRepartitionFlux : null,
      ],

      currentAllocationAmountStock: [
        item && item.currentAllocationAmountStock
          ? item.currentAllocationAmountStock
          : null,
      ],

      // PRE-4896 : cette slide n'affiche ni ne modifie ces 8 champs, mais doit les faire transiter
      // tels quels (préremplis par la commande COMPLETE_SUPPORT_RATES au submit de cette slide) —
      // sans cela, l'extract() écraserait leur valeur à null avant même que la slide
      // "tauxParSupportArbitrage" ait pu les afficher.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      upfrontAssureurSaisi: [item?.upfrontAssureurSaisi ?? null],
      upfrontEmetteurSaisi: [item?.upfrontEmetteurSaisi ?? null],
      fraisGestionSaisi: [item?.fraisGestionSaisi ?? null],
      retrocessionAssureurSaisi: [item?.retrocessionAssureurSaisi ?? null],
    };
  }

  private _synced = new WeakSet<UntypedFormGroup>();
  private _redistributing = false;

  /**
   * Largest-remainder (Hamilton) allocation.
   * Returns amounts (in euros, 2 decimals) such that, when sum(percents) == 100,
   * sum(amounts) == totalEuros EXACTLY. Cents lost/gained to rounding are
   * distributed to the rows whose unrounded value had the largest/smallest
   * fractional remainder. When sum(percents) != 100, each amount is rounded
   * independently and the sum may differ from totalEuros (by design — the
   * user has not allocated the full amount yet).
   */
  private allocateAmounts(percents: number[], totalEuros: number): number[] {
    if (!percents.length) return [];
    if (totalEuros <= 0) return percents.map(() => 0);

    const totalCents = Math.round(totalEuros * 100);
    const sumP = percents.reduce((s, p) => s + (Number(p) || 0), 0);

    // Per-row exact value in cents (unrounded).
    const exact = percents.map(p => (totalCents * (Number(p) || 0)) / 100);
    const rounded = exact.map(e => Math.round(e));

    const isFullyAllocated =
      Math.abs(sumP - 100) < FULL_PERCENT_TOLERANCE;

    if (!isFullyAllocated) {
      console.log(
        '[allocateAmounts] sumP=%s%% (not full) → per-row rounding, sum may drift from total',
        sumP.toFixed(4),
      );
      return rounded.map(c => c / 100);
    }

    const sumRounded = rounded.reduce((s, v) => s + v, 0);
    const diff = totalCents - sumRounded;

    if (diff === 0) {
      console.log(
        '[allocateAmounts] sumP=100, totalCents=%d, no rounding drift',
        totalCents,
      );
      return rounded.map(c => c / 100);
    }

    console.log(
      '[allocateAmounts] sumP=100, totalCents=%d, sumRounded=%d, diff=%d → largest-remainder redistribution',
      totalCents,
      sumRounded,
      diff,
    );

    // Index rows by their fractional remainder.
    const indexed = exact.map((e, i) => ({
      idx: i,
      frac: e - Math.floor(e),
    }));

    if (diff > 0) {
      // Need to add `diff` cents → give to rows with the largest fractional part.
      indexed.sort((a, b) => b.frac - a.frac || a.idx - b.idx);
      for (let k = 0; k < diff && k < indexed.length; k++) {
        rounded[indexed[k].idx] += 1;
      }
    } else {
      // Need to remove |diff| cents → take from rows with the smallest fractional part.
      indexed.sort((a, b) => a.frac - b.frac || a.idx - b.idx);
      for (let k = 0; k < -diff && k < indexed.length; k++) {
        rounded[indexed[k].idx] = Math.max(0, rounded[indexed[k].idx] - 1);
      }
    }

    return rounded.map(c => c / 100);
  }

  /**
   * Reads the current percent on every row, computes the new amounts via
   * largest-remainder allocation, and writes them back without re-emitting
   * value changes (so we don't recurse into the amount→percent handler).
   */
  private recomputeAllAmountsFromPercents(
    supportsInv: UntypedFormArray,
    totalEuros: number,
  ) {
    if (this._redistributing) return;
    this._redistributing = true;
    try {
      const percents = supportsInv.controls.map(
        c => Number(c.get('repartitionStock')?.value) || 0,
      );
      const amounts = this.allocateAmounts(percents, totalEuros);

      supportsInv.controls.forEach((c, i) => {
        const amountCtrl = c.get('amountRepartitionStock');
        if (!amountCtrl) return;
        if (Number(amountCtrl.value) !== amounts[i]) {
          amountCtrl.setValue(amounts[i], { emitEvent: false });
        }
      });

      const sumEuros = amounts.reduce((s, v) => s + v, 0);
      const sumP = percents.reduce((s, p) => s + p, 0);
      console.log(
        '[recomputeAllAmounts] total=%s€ | sum%%=%s | sum€=%s | percents=%o | amounts=%o',
        totalEuros.toFixed(2),
        sumP.toFixed(4),
        sumEuros.toFixed(2),
        percents,
        amounts,
      );
    } finally {
      this._redistributing = false;
    }
  }

  private attachReinvestmentSyncProduct(product: UntypedFormGroup) {
    const params = product.get('parameters') as UntypedFormGroup;
    if (!params) return;

    const supportsInv = params.get(
      'supportsInvestmentStock',
    ) as UntypedFormArray;
    const supportsDiv = params.get('supportsDivestment') as UntypedFormArray;

    if (!supportsInv) return;

    const total = () => totalToReinvest(supportsDiv?.value as any[]);

    const attachOne = (ctrl: UntypedFormGroup, idx: number) => {
      if (this._synced.has(ctrl)) return;
      this._synced.add(ctrl);

      const percentCtrl = ctrl.get('repartitionStock');
      const amountCtrl = ctrl.get('amountRepartitionStock');
      if (!percentCtrl || !amountCtrl) return;

      let syncing = false;

      percentCtrl.valueChanges.subscribe((raw: any) => {
        if (syncing) return;
        syncing = true;
        try {
          let p = Number(String(raw).replace(',', '.'));
          if (isNaN(p)) p = 0;
          p = Math.max(0, Math.min(100, p));
          const p4 = Number(p.toFixed(4));
          if (p4 !== Number(raw)) {
            percentCtrl.setValue(p4, { emitEvent: false });
          }

          console.log(
            '[%% changed] row=%d raw=%o → p4=%s → recompute all amounts',
            idx,
            raw,
            p4,
          );
          // Recompute amounts on every row so the global total stays exact.
          this.recomputeAllAmountsFromPercents(supportsInv, total());
        } finally {
          syncing = false;
        }
      });

      amountCtrl.valueChanges.subscribe((raw: any) => {
        if (syncing) return;
        syncing = true;
        try {
          const t = total();
          let amt = Number(String(raw).replace(',', '.'));
          if (isNaN(amt)) amt = 0;
          amt = Math.max(0, Math.min(t, amt));
          const amt2 = Number(amt.toFixed(2));
          if (amt2 !== Number(raw)) {
            amountCtrl.setValue(amt2, { emitEvent: false });
          }

          // Manual € edit → only this row's percent is updated to preserve ratio.
          // Other rows stay untouched so the user can fine-tune one cell.
          const p = t > 0 ? (amt2 / t) * 100 : 0;
          const p4 = Number(Math.max(0, Math.min(100, p)).toFixed(4));
          percentCtrl.setValue(p4, { emitEvent: false });
          percentCtrl.updateValueAndValidity({ emitEvent: false });

          console.log(
            '[€ changed] row=%d raw=%o → amt2=%s → p4=%s (this row only)',
            idx,
            raw,
            amt2,
            p4,
          );
        } finally {
          syncing = false;
        }
      });

      // Normalize the loaded value once on attach (4 decimals, clamped).
      const initP = Number(
        Math.max(0, Math.min(100, Number(percentCtrl.value) || 0)).toFixed(4),
      );
      percentCtrl.setValue(initP, { emitEvent: true });
    };

    supportsInv.controls.forEach((c, i) =>
      attachOne(c as UntypedFormGroup, i),
    );
    supportsInv.valueChanges.subscribe(() => {
      supportsInv.controls.forEach((c, i) =>
        attachOne(c as UntypedFormGroup, i),
      );
    });

    if (supportsDiv && supportsDiv.valueChanges) {
      supportsDiv.valueChanges.subscribe(() => {
        console.log(
          '[supportsDiv changed] new total=%s€ → recompute all amounts',
          total().toFixed(2),
        );
        this.recomputeAllAmountsFromPercents(supportsInv, total());
      });
    }
  }

  get arbitrageType(): string {
    return this.form
      .get('products')
      .get(`${0}`)
      .get('parameters')
      .get('arbitrageType').value;
  }

  get amountToReinvestStock(): number {
    return totalToReinvest(
      this.form
        .get('products')
        .get(`${0}`)
        .get('parameters')
        .get('supportsDivestment').value,
    );
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportsInvestmentStock() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportsInvestmentStock') as UntypedFormArray;
  }
  get productId() {
    return this.form.get('products').get(`${0}`).get('productId').value;
  }

  get totalRepartitionPercent(): number {
    const inv = this.supportsInvestmentStock;
    if (!inv) return 0;
    return inv.controls.reduce(
      (acc, c) => acc + (Number(c.get('repartitionStock')?.value) || 0),
      0,
    );
  }

  get remainingPercent(): number {
    return roundTo(100 - this.totalRepartitionPercent, 4);
  }

  get totalRepartitionAmount(): number {
    const inv = this.supportsInvestmentStock;
    if (!inv) return 0;
    return inv.controls.reduce(
      (acc, c) => acc + (Number(c.get('amountRepartitionStock')?.value) || 0),
      0,
    );
  }

  get remainingAmount(): number {
    return roundTo(
      (this.amountToReinvestStock || 0) - this.totalRepartitionAmount,
      2,
    );
  }

  get isFullyReinvested(): boolean {
    return (
      Math.abs(this.totalRepartitionPercent - 100) < FULL_PERCENT_TOLERANCE &&
      Math.abs(this.remainingAmount) < 0.005
    );
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      let supportsInvestmentStock = _.cloneDeep(
        product.parameters.supportsInvestmentStock,
      );

      supportsInvestmentStock.forEach(support => {
        support.repartitionStock = !_.isNil(support.repartitionStock)
          ? Number(support.repartitionStock) / 100
          : 0;
      });

      product.parameters.supportsInvestmentStock = supportsInvestmentStock;
    });
    return products;
  }
}
